/*
 * PWA / Web Push do Perabit.
 *
 * Por que existe: no iPhone o push só funciona se o site tiver Service Worker
 * e manifest, e a permissão tiver sido pedida NUM TOQUE do usuário — o iOS
 * ignora `Notification.requestPermission()` chamado sozinho. Além disso, no
 * iOS o push só é entregue com o app instalado na Tela de Início (Safari
 * aberto na aba não recebe). É isso que este módulo cuida.
 */

import api from "./api";

const urlBase64ParaUint8Array = (base64) => {
  const padding = "=".repeat((4 - (base64.length % 4)) % 4);
  const normalizado = (base64 + padding).replace(/-/g, "+").replace(/_/g, "/");
  const bruto = window.atob(normalizado);
  const saida = new Uint8Array(bruto.length);
  for (let i = 0; i < bruto.length; i += 1) saida[i] = bruto.charCodeAt(i);
  return saida;
};

export const ehIos = () =>
  typeof navigator !== "undefined" && /iphone|ipad|ipod/i.test(navigator.userAgent);

export const estaInstalado = () =>
  typeof window !== "undefined" &&
  (window.matchMedia("(display-mode: standalone)").matches ||
    window.navigator.standalone === true);

export const pushSuportado = () =>
  typeof window !== "undefined" &&
  "serviceWorker" in navigator &&
  "PushManager" in window &&
  "Notification" in window;

// No iPhone, push só com o app na Tela de Início. Sem isso o iOS aceita a
// chamada e não entrega nada — melhor avisar do que fingir que deu certo.
export const pushPrecisaInstalar = () => ehIos() && !estaInstalado();

export const permissaoPush = () =>
  typeof window !== "undefined" && "Notification" in window
    ? Notification.permission
    : "denied";

const obterChavePublica = async () => {
  const { data } = await api.get("/push/vapid-public-key");
  const chave = data?.publicKey;
  if (!chave) throw new Error("ERR_PUSH_SEM_VAPID");
  return chave;
};

/*
 * Nunca usa `navigator.serviceWorker.ready` direto: essa promise NUNCA
 * rejeita e nunca resolve enquanto não existir worker ativo. Em desenvolvimento
 * (onde o SW não é registrado de propósito) um `await` nela travaria — e no
 * logout isso significaria travar o "Sair" para sempre.
 */
const obterRegistro = async () => {
  if (typeof navigator === "undefined" || !("serviceWorker" in navigator)) {
    return null;
  }

  try {
    const registro = await navigator.serviceWorker.getRegistration();
    return registro || null;
  } catch (_) {
    return null;
  }
};

const gravarInscricaoNoServidor = async (inscricao) => {
  const json = inscricao.toJSON();
  await api.post("/push/subscribe", {
    endpoint: json.endpoint,
    p256dh: json.keys?.p256dh,
    auth: json.keys?.auth
  });
};

export const pushInscritoNesteAparelho = async () => {
  if (!pushSuportado()) return false;
  try {
    const registro = await obterRegistro();
    if (!registro) return false;
    const inscricao = await registro.pushManager.getSubscription();
    return Boolean(inscricao);
  } catch (_) {
    return false;
  }
};

// Precisa ser chamado dentro de um clique/toque do usuário.
export const ativarPush = async () => {
  if (!pushSuportado()) throw new Error("ERR_PUSH_NAO_SUPORTADO");
  if (pushPrecisaInstalar()) throw new Error("ERR_PUSH_PRECISA_INSTALAR");

  const permissao = await Notification.requestPermission();
  if (permissao !== "granted") throw new Error("ERR_PUSH_NEGADO");

  const registro = await obterRegistro();
  if (!registro) throw new Error("ERR_PUSH_NAO_SUPORTADO");

  const chave = await obterChavePublica();

  let inscricao = await registro.pushManager.getSubscription();
  if (!inscricao) {
    inscricao = await registro.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ParaUint8Array(chave)
    });
  }

  await gravarInscricaoNoServidor(inscricao);
  return true;
};

// Depois do primeiro "Ativar", a permissão já está concedida — dá para
// reassinar sozinho a cada login, sem depender de outro toque.
export const reassinarPushSeAutorizado = async () => {
  if (!pushSuportado()) return false;
  if (permissaoPush() !== "granted") return false;

  try {
    const registro = await obterRegistro();
    if (!registro) return false;

    const chave = await obterChavePublica();

    let inscricao = await registro.pushManager.getSubscription();
    if (!inscricao) {
      inscricao = await registro.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ParaUint8Array(chave)
      });
    }

    await gravarInscricaoNoServidor(inscricao);
    return true;
  } catch (_) {
    return false;
  }
};

/*
 * Sai do aparelho, não só da sessão: sem isso a próxima pessoa que logar
 * neste celular continuaria recebendo notificação da conta anterior — o que
 * fura o isolamento entre usuários e empresas.
 */
export const desativarPush = async () => {
  if (!pushSuportado()) return;

  try {
    const registro = await obterRegistro();
    if (!registro) return;

    const inscricao = await registro.pushManager.getSubscription();
    if (!inscricao) return;

    const endpoint = inscricao.endpoint;

    try {
      await api.post("/push/unsubscribe", { endpoint });
    } catch (_) {}

    await inscricao.unsubscribe();
  } catch (_) {}
};
